import { useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, A11y } from "swiper/modules";
import { ArrowNarrowLeft, ArrowNarrowRight } from "untitledui-js/react";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { ImageLightboxItem } from "../types/ImageTypes";

import "swiper/css";
import "swiper/css/navigation";
import { ImageLightbox } from "@components/ImageLightbox";

interface ImageCarouselProps {
  images: ImageLightboxItem[];
}

export default function ImageCarousel({ images }: ImageCarouselProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState<ImageLightboxItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0);

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const paginationRef = useRef(null);

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div className="group relative mx-auto my-14 w-full max-w-6xl overflow-hidden">
      <Swiper
        modules={[Navigation, Pagination, A11y]}
        slidesPerView={"auto"}
        spaceBetween={8}
        loop={false}
        initialSlide={0}
        navigation={{
          prevEl: prevRef.current,
          nextEl: nextRef.current,
        }}
        pagination={{
          el: paginationRef.current,
          clickable: true,
          renderBullet: (_, className) =>
            `<span class="${className} w-2 lg:w-3 h-2 lg:h-3 rounded-full bg-tertiary-200 dark:bg-white/30 block transition-all duration-300 ease-in-out"></span>`,
        }}
        a11y={{ enabled: true }}
        onSlidesUpdated={(swiper) => {
          // Ensure navigation parameters exist and are not boolean
          swiper.params.navigation = swiper.params.navigation ?? {};
          if (
            swiper.params.navigation &&
            typeof swiper.params.navigation !== "boolean"
          ) {
            // Assign the previous and next button elements to Swiper's navigation
            swiper.params.navigation.prevEl = prevRef.current;
            swiper.params.navigation.nextEl = nextRef.current;
            swiper.navigation.init();
          }

          // Ensure pagination parameters exist and are not boolean
          if (
            swiper.params.pagination &&
            typeof swiper.params.pagination !== "boolean"
          ) {
            // Assign the pagination element to Swiper's pagination
            swiper.params.pagination.el = paginationRef.current;
            swiper.pagination.init();
            swiper.pagination.render();
            swiper.pagination.update();
          }
        }}
      >
        {images.map((img, index) => (
          <SwiperSlide
            key={img.id}
            className="flex justify-center md:!w-[800px]"
          >
            <button
              onClick={() => {
                setIsModalOpen(true);
                setModalImage(img);
                setCurrentImageIndex(index);
              }}
              className="h-[300px] w-full hover:cursor-pointer md:h-[450px] lg:h-[550px]"
            >
              <img
                src={img.image}
                alt={img.alt}
                className="h-full w-full object-cover"
              />
            </button>
            {img.caption && (
              <Typography
                size="sm"
                className="leading-[100%] font-normal text-tertiary-900 dark:text-tertiary-100"
              >
                {img.caption}
              </Typography>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="opacity-0 *:absolute *:top-1/2 *:z-10 *:flex *:h-10 *:w-10 *:-translate-y-1/2 *:items-center *:justify-center *:rounded-full *:bg-primary-500 group-hover:opacity-100 *:hover:bg-primary-400 *:lg:h-12 *:lg:w-12">
        <div
          ref={prevRef}
          className="pointer-events-auto left-4 lg:left-1/2 lg:-translate-x-[calc(--spacing(96)+--spacing(7))]"
        >
          <ArrowNarrowLeft
            data-testid="arrow-prev"
            className="stroke-secondary-700"
            strokeWidth={1}
          />
        </div>

        <div
          ref={nextRef}
          className="pointer-events-auto right-4 lg:left-1/2 lg:translate-x-[calc(--spacing(96)---spacing(5))]"
        >
          <ArrowNarrowRight
            data-testid="arrow-next"
            className="stroke-secondary-700"
            strokeWidth={1}
          />
        </div>
      </div>

      <div
        ref={paginationRef}
        data-testid="swiper-pagination"
        className="mt-3 flex justify-center gap-1 lg:mt-4"
      />

      {isModalOpen && modalImage && (
        <ImageLightbox
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          modalImage={modalImage}
          setModalImage={setModalImage}
          images={images}
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
        />
      )}
    </div>
  );
}
